import React from "react";
import { dropDown } from "../../asset";
import useDropdown from "../../util/hooks/useDropDown";
import ViewEventPopup from "./ViewEventPopUp";
import CreateEvent from "../../components/modal/CreateEvent";
import { useBranchCode } from "../../util/hooks/useBranch";
import { EventData } from "../../types/eventType";
import CalendarAndEventApis from "../../api/CalendarAndEventApis";
import { useLocation } from "react-router-dom";

const eventStyles = [
  {
    bg: "bg-primary-50 text-primary-600",
    border: "border-primary-600",
  },
  {
    bg: "bg-error-50 text-error-700",
    border: "border-error-500",
  },
  {
    bg: "bg-success-50 text-success-700",
    border: "border-success-500",
  },
  {
    bg: "bg-warning-50 text-warning-700",
    border: "border-warning-500",
  },
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export default function Calendar() {
  const [current, setCurrent] = React.useState(new Date());
  const [selectedEvent, setSelectedEvent] = React.useState<EventData | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = React.useState<string>("");
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [popupPosition, setPopupPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const { isOpen, toggleDropdown, closeDropdown, dropdownRef } =
    useDropdown(false);

  const hoverTimeout = React.useRef<number | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);

  const year = current.getFullYear();
  const month = current.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const startDay = new Date(year, month, 1).getDay(); // 0 = Sun

  const monthLabel = current.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () => setCurrent(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrent(new Date(year, month + 1, 1));

  // Convert Sunday-first to Monday-first
  const leadingEmptyDays = startDay === 0 ? 6 : startDay - 1;

  let globalEventIndex = 0;

  const OFFSET = 8;

  const branchCode = useBranchCode();
  const location = useLocation();

  const [events, setEvents] = React.useState<Record<string, EventData[]>>({});

  const fetchEvents = React.useCallback(async () => {
    if (!branchCode) return;
    const yearMonth = `${year}-${String(month + 1).padStart(2, "0")}`;

    const response = await CalendarAndEventApis.getMonthlyEvents(
      branchCode,
      yearMonth,
    );
    if (response?.status === 1) {
      setEvents(response.data || {});
    }
  }, [branchCode, year, month]);

  React.useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  React.useEffect(() => {
    if (location.state?.refresh) {
      fetchEvents();
    }
  }, [location.state]);

  return (
    <div className="max-w-7xl mx-auto flex flex-col">
      <div className="flex gap-2 items-center justify-center mb-[18px]">
        <button onClick={prevMonth} className="">
          <img src={dropDown} alt="" className="rotate-90 w-4 h-4" />
        </button>

        <h2 className="text-2xl font-medium text-neutral-500">{monthLabel}</h2>

        <button onClick={nextMonth}>
          <img src={dropDown} alt="" className="-rotate-90 w-4 h-4" />
        </button>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 text-sm text-neutral-500 font-medium bg-background-300 py-3 rounded-t-xl">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <div key={day} className="text-center">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 border rounded-b-xl border-neutral-50 overflow-hidden">
        {/* Empty cells before month starts */}
        {Array.from({ length: leadingEmptyDays }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="h-28 border border-neutral-50 bg-background-300"
          />
        ))}

        {/* Month days */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateKey = `${year}-${String(month + 1).padStart(
            2,
            "0",
          )}-${String(day).padStart(2, "0")}`;

          return (
            <div
              key={day}
              onClick={() => {
                setSelectedDate(dateKey);
                setIsCreateOpen(true);
              }}
              className="h-28 border border-neutral-50 cursor-pointer hover:bg-background-100 flex flex-col"
            >
              <div className="text-base text-neutral-400 p-3 text-right">
                {day}
              </div>

              <div className="mt-auto">
                {events[dateKey]?.map((event) => {
                  const style =
                    eventStyles[globalEventIndex++ % eventStyles.length];

                  return (
                    <div
                      ref={triggerRef}
                      key={event.eventId}
                      onMouseEnter={(e) => {
                        if (hoverTimeout.current) {
                          clearTimeout(hoverTimeout.current);
                          hoverTimeout.current = null;
                        }

                        const rect = e.currentTarget.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;

                        const top =
                          spaceBelow >= 320
                            ? rect.bottom + OFFSET
                            : rect.top - 320 - OFFSET;

                        setSelectedEvent(event);
                        setPopupPosition({
                          top,
                          left: Math.min(rect.left, window.innerWidth - 300),
                        });

                        if (!isOpen) toggleDropdown();
                      }}
                      onMouseLeave={(e) => {
                        const next = e.relatedTarget as Node | null;
                        if (dropdownRef.current?.contains(next)) return;

                        hoverTimeout.current = window.setTimeout(() => {
                          closeDropdown();
                        }, 150);
                      }}
                      className={`px-2 py-2 capitalize text-xs cursor-pointer text-center w-full border-l-[6px] ${style.bg} ${style.border}`}
                    >
                      {event.title}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {isOpen && selectedEvent && (
        <ViewEventPopup
          event={selectedEvent}
          onClose={closeDropdown}
          dropdownRef={dropdownRef}
          position={popupPosition}
        />
      )}

      {isCreateOpen && (
        <CreateEvent
          onClose={() => setIsCreateOpen(false)}
          selectedDate={selectedDate}
          onSuccess={() => {
            fetchEvents();
            setIsCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}
